import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ListAltIcon from '@mui/icons-material/ListAlt';

import { useApiGet } from '../../../utils/httpClient';
import { getDonationList } from '../../../endpoints/donations';
import { getSPDChaptersList } from '../../../endpoints/chaptersTs';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';
import PageHeader from '../../../Components/PageHeader';
import CreateAckBatchDialog from './CreateAckBatchDialog';
import ServerSideTable from '../../../Components/ServerSideTable';
import DonationsChapterSelector from '../../../Components/DonationChapters';
import ExportButton from '../../../Components/ExportButton';

const ACK_WORKFLOW_OPTIONS = [
  { value: '', label: 'All ack statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'complete', label: 'Complete' },
  { value: 'legacy_complete', label: 'Legacy complete' },
  { value: 'waived', label: 'Waived' },
  { value: 'not_required', label: 'Not required' },
];

const ackStatusLabel = (value) =>
  ACK_WORKFLOW_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '—';

export default function DonationsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === '' || value == null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    searchParams.get('search') || ''
  );
  const [ackStatus, setAckStatus] = useState(
    searchParams.get('ack_workflow_status') || ''
  );
  const [localChapterId, setLocalChapterId] = useState(
    searchParams.get('local_chapter_id') || ''
  );
  const [spdChapterId, setSpdChapterId] = useState(
    searchParams.get('spd_chapter_id') || ''
  );
  const [createAckBatchOpen, setCreateAckBatchOpen] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      updateSearchParams({ search: delaySearch });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch, updateSearchParams]);

  const { data: permissions } = useApiGet(
    ['my-permissions-donations-list'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );
  const canManage = hasUserApplicationPermission(
    permissions,
    'donations:manage'
  );
  const canLegacy = hasUserApplicationPermission(
    permissions,
    'donations:legacy'
  );
  const canEnter = canManage || canLegacy;

  const { data: spdChapters = [] } = useApiGet(
    ['donations-spd-chapters'],
    getSPDChaptersList,
    { enabled: true, retry: 1 }
  );

  const listParams = useMemo(
    () => ({
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      ack_workflow_status: ackStatus || undefined,
      local_chapter_id: localChapterId || undefined,
      spd_chapter_id: spdChapterId || undefined,
    }),
    [
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      ackStatus,
      localChapterId,
      spdChapterId,
    ]
  );

  const { data: listResponse, isFetching } = useApiGet(
    ['donations', listParams],
    () => getDonationList(listParams),
    { enabled: true, retry: 1, refetchOnMount: 'always' }
  );

  const rows = useMemo(() => listResponse?.data ?? [], [listResponse?.data]);
  const pagination = listResponse?.pagination;

  const columns = useMemo(
    () => [
      {
        id: 'full_name',
        label: 'Name',
        firstCell: true,
        width: '18%',
        sortable: false,
      },
      {
        id: 'amount',
        label: 'Amount',
        width: '10%',
        sortable: true,
      },
      {
        id: 'created_at',
        label: 'Created',
        width: '10%',
        sortable: true,
      },
      {
        id: 'ack_workflow_status',
        label: 'Ack status',
        width: '12%',
        sortable: true,
        render: (v) => ackStatusLabel(v),
      },
      {
        id: 'local_chapter',
        label: 'Local chapter',
        width: '12%',
        sortable: false,
        render: (v) => v ?? '—',
      },
      {
        id: 'spd_chapter',
        label: 'SPD',
        width: '12%',
        sortable: false,
        render: (v) => v ?? '—',
      },
      {
        id: 'behalf_of_summary',
        label: 'IHO / IMO',
        width: '26%',
        sortable: false,
        render: (v) => v || '—',
      },
    ],
    []
  );

  const exportColumns = useMemo(
    () =>
      columns.map((c) => ({
        key: String(c.id),
        label: c.label,
      })),
    [columns]
  );

  const exportRows = useMemo(
    () =>
      rows.map((row) => ({
        full_name: row.full_name,
        amount: row.amount,
        created_at: row.created_at,
        ack_workflow_status: ackStatusLabel(row.ack_workflow_status),
        local_chapter: row.local_chapter ?? '',
        spd_chapter: row.spd_chapter ?? '',
        behalf_of_summary: row.behalf_of_summary ?? '',
      })),
    [rows]
  );

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader title="Donations" />
      </Grid>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {canEnter && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/internal/donations/new')}
            >
              New donation
            </Button>
          )}
          {canManage && (
            <>
              <Button
                variant="outlined"
                startIcon={<ListAltIcon />}
                onClick={() => navigate('/internal/donations/ack-batches')}
              >
                Acknowledgement batches
              </Button>
              <Button
                variant="outlined"
                startIcon={<PlaylistAddIcon />}
                onClick={() => setCreateAckBatchOpen(true)}
              >
                New acknowledgement batch
              </Button>
            </>
          )}
          <ExportButton
            data={exportRows}
            columns={exportColumns}
            filename={`donations-${new Date().toISOString().slice(0, 10)}`}
            excelOnly
            exportAllData={false}
            disabled={rows.length === 0}
            loading={isFetching}
          />
        </Stack>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={delaySearch}
            fullWidth
            onChange={(e) => setDelaySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Ack status"
            size="small"
            fullWidth
            value={ackStatus}
            onChange={(e) => {
              const v = e.target.value;
              setAckStatus(v);
              setPage(0);
              updateSearchParams({ ack_workflow_status: v });
            }}
          >
            {ACK_WORKFLOW_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <DonationsChapterSelector
            label="Local chapter"
            size="small"
            chapter={localChapterId || null}
            handleChapterSelect={(id) => {
              const v = id || '';
              setLocalChapterId(v);
              setPage(0);
              updateSearchParams({ local_chapter_id: v });
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            size="small"
            options={spdChapters}
            getOptionLabel={(o) => o.name ?? ''}
            value={spdChapters.find((s) => s.id === spdChapterId) ?? null}
            onChange={(_, nv) => {
              const v = nv?.id ?? '';
              setSpdChapterId(v);
              setPage(0);
              updateSearchParams({ spd_chapter_id: v });
            }}
            renderInput={(params) => (
              <TextField {...params} label="SPD chapter" />
            )}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <ServerSideTable
          data={rows}
          columns={columns}
          loading={isFetching}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={pagination?.total_count ?? 0}
          totalPages={pagination?.total_pages ?? 0}
          hasNext={pagination?.has_next ?? false}
          hasPrev={pagination?.has_prev ?? false}
          order={order}
          orderBy={orderBy}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
          onSort={handleSort}
          onRowClick={(row) => navigate(`/internal/donations/${row.id}`)}
          emptyMessage="No donations match your filters."
        />
      </Grid>
      <CreateAckBatchDialog
        open={createAckBatchOpen}
        onClose={() => setCreateAckBatchOpen(false)}
        onCreated={(res) => {
          if (res?.batch?.id) {
            navigate(`/internal/donations/ack-batches/${res.batch.id}`);
          }
        }}
      />
    </Grid>
  );
}
